/**
 * Exit-intent detection and the passive marketing-agent surface contract.
 *
 * This is deliberately separate from `requestDemo()`. In website-agent mode,
 * `requestDemo()` starts a paid Pipecat session and probes the microphone. A
 * mouse leaving the page is not a user gesture, so exit intent may only reveal
 * the dormant agent. The visitor's next click/type remains the session gate.
 */

import { hasMatchMedia, realMatchMedia } from './native-viewport';

/** The internal event both marketing surfaces use to reveal their idle UI. */
export const HOST_EXIT_INTENT_EVENT = 'floe:show-exit-intent';

/** Default dwell before a top-edge mouseout is eligible. */
export const EXIT_INTENT_MIN_TIME_ON_PAGE_MS = 5_000;

/** Allow a small browser-coordinate tolerance around the top edge. */
export const EXIT_INTENT_THRESHOLD_PX = 20;

/** Automatic detection needs the primary input to behave like a desktop mouse. */
export const EXIT_INTENT_POINTER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';

const MAX_EXIT_INTENT_MESSAGE_CHARS = 240;
const MAX_EXIT_INTENT_DELAY_MS = 60 * 60 * 1_000;
const MAX_EXIT_INTENT_THRESHOLD_PX = 100;

export interface ExitIntentOptions {
  /** Optional first line shown by the website agent. */
  message?: string;
  /** Minimum dwell before automatic desktop detection. Default: 5000ms. */
  minTimeOnPageMs?: number;
  /** Top-edge tolerance in CSS pixels. Default: 20px. */
  thresholdPx?: number;
}

/** Declarative `Floe()` config. An object opts in unless `enabled` is false. */
export interface ExitIntentConfig extends ExitIntentOptions {
  enabled?: boolean;
}

export interface ExitIntentSurfaceOptions {
  message?: string;
}

/**
 * Customer snippets are plain JavaScript, so normalize instead of trusting the
 * public TypeScript type. Long copy is capped before it reaches widget state.
 */
export function normalizeExitIntentMessage(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, MAX_EXIT_INTENT_MESSAGE_CHARS) : undefined;
}

function finiteNumber(value: unknown, fallback: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(0, value));
}

export function normalizeExitIntentOptions(value: unknown): Required<
  Pick<ExitIntentOptions, 'minTimeOnPageMs' | 'thresholdPx'>
> & Pick<ExitIntentOptions, 'message'> {
  const raw =
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};

  return {
    message: normalizeExitIntentMessage(raw.message),
    minTimeOnPageMs: finiteNumber(
      raw.minTimeOnPageMs,
      EXIT_INTENT_MIN_TIME_ON_PAGE_MS,
      MAX_EXIT_INTENT_DELAY_MS,
    ),
    thresholdPx: finiteNumber(
      raw.thresholdPx,
      EXIT_INTENT_THRESHOLD_PX,
      MAX_EXIT_INTENT_THRESHOLD_PX,
    ),
  };
}

/** Resolve an opt-in config to safe options; null means disabled. */
export function resolveExitIntentConfig(value: unknown): ReturnType<
  typeof normalizeExitIntentOptions
> | null {
  if (value === true) return normalizeExitIntentOptions(undefined);
  if (value === false || value === null || value === undefined) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return null;
  if ((value as Record<string, unknown>).enabled === false) return null;
  return normalizeExitIntentOptions(value);
}

/** Pull normalized custom copy from the cancelable surface event. */
export function exitIntentMessageFromEvent(event: Event): string | undefined {
  if (typeof CustomEvent === 'undefined' || !(event instanceof CustomEvent)) return undefined;
  return normalizeExitIntentMessage(
    event.detail && typeof event.detail === 'object'
      ? (event.detail as Record<string, unknown>).message
      : undefined,
  );
}

/**
 * Reveal whichever marketing agent is mounted without starting a session.
 * Returns true only when a surface claimed the cancelable event.
 */
export function surfaceExitIntent(options: ExitIntentSurfaceOptions = {}): boolean {
  if (typeof window === 'undefined') return false;
  return !window.dispatchEvent(
    new CustomEvent(HOST_EXIT_INTENT_EVENT, {
      cancelable: true,
      detail: { message: normalizeExitIntentMessage(options.message) },
    }),
  );
}

/** Fail closed when pointer capability is unavailable or touch-first. */
export function supportsAutomaticExitIntent(): boolean {
  if (!hasMatchMedia()) return false;
  return !!realMatchMedia(EXIT_INTENT_POINTER_MEDIA_QUERY)?.matches;
}

/** `performance.now()` is elapsed time since this document's time origin. */
function elapsedDocumentLifetimeMs(target: Document): number {
  const elapsed = target.defaultView?.performance?.now();
  return typeof elapsed === 'number' && Number.isFinite(elapsed)
    ? Math.max(0, elapsed)
    : 0;
}

/**
 * One-shot desktop detector. A failed surface attempt does not consume the
 * shot, which lets a listener that mounts a frame later handle the next exit.
 */
export class ExitIntentDetector {
  private enabled = false;
  private armed = false;
  private fired = false;
  private armTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly options: ReturnType<typeof normalizeExitIntentOptions>;

  constructor(
    private readonly onIntent: () => boolean,
    options?: ExitIntentOptions,
    private readonly target: Document = document,
  ) {
    this.options = normalizeExitIntentOptions(options);
  }

  enable(): void {
    if (this.enabled || this.fired) return;
    if (!supportsAutomaticExitIntent()) return;
    this.enabled = true;
    this.target.addEventListener('mouseout', this.handleMouseOut);

    const remainingDwellMs = Math.max(
      0,
      this.options.minTimeOnPageMs - elapsedDocumentLifetimeMs(this.target),
    );
    if (remainingDwellMs <= 0) {
      this.armed = true;
      return;
    }
    this.armTimer = setTimeout(() => {
      this.armTimer = null;
      this.armed = true;
    }, remainingDwellMs);
  }

  disable(): void {
    if (this.enabled) {
      this.target.removeEventListener('mouseout', this.handleMouseOut);
    }
    this.enabled = false;
    this.armed = false;
    if (this.armTimer !== null) {
      clearTimeout(this.armTimer);
      this.armTimer = null;
    }
  }

  private readonly handleMouseOut = (event: MouseEvent): void => {
    if (!this.enabled || !this.armed || this.fired) return;
    if (this.target.visibilityState !== 'visible') return;
    if (event.relatedTarget !== null) return;
    if (event.clientY > this.options.thresholdPx) return;

    if (!this.onIntent()) return;
    this.fired = true;
    this.disable();
  };
}
